import { screenShare, vb } from "../../../libs/roomnew";
import { applyPerformanceSetting } from "./actions";
import { FRAMERATE_DROP_THRESHOLD_PERCENT, FRAMERATE_RECOVERY_CHECKS, FRAMERATE_RECOVERY_THRESHOLD_PERCENT, PERFORMANCE_FRAME_RATE, RECOVER_COUNT_RESET_CHECKS, STOP_RECOVER_THRESHOLD, VB_FRAMERATE_DROP_THRESHOLD_PERCENT, VBG_PERFORMANCE_FRAME_RATE } from "./constants";
import { getPerformanceState, getSenderFrameRate } from "./functions";

export class LoadMonitor {
    constructor() {
        this.thresholds = FRAMERATE_DROP_THRESHOLD_PERCENT;
        this.maxFramerate = PERFORMANCE_FRAME_RATE.high;
        this.checksToAverage = 5;
        this.checkCount = 0;
        this.framerateSum = 0;
        this.lastFramerate = null;

        this.succcessiveRecoveryChecks = 0;
        this.recoversCount=0;
        this.allowRecover=true;
        // Counts consecutive checks where the framerate held at the current
        // tier without a drop. Once this reaches RECOVER_COUNT_RESET_CHECKS we
        // treat the CPU as recovered and re-arm auto-recovery so a transient
        // spike earlier in the session does not permanently pin the sender at a
        // degraded tier (which keeps the bridge BWE low for all receivers).
        this.stableChecks=0;
    }

    changeOptimizer(framerate) {
        if(framerate === undefined || screenShare.isSharing()){
            // console.log("Framerate is undefined or screen sharing is active, skipping check");
            this.checkCount = 0;
            this.framerateSum = 0;
            this.succcessiveRecoveryChecks = 0;
            return;
        }
        if (this.checkCount < this.checksToAverage) {
            this.framerateSum += framerate ?? 0;
            this.checkCount++;
        } else {
            const {setting: currentSetting} = getPerformanceState();
            if(vb.effectPlaying){
                this.maxFramerate=currentSetting == 3 ? VBG_PERFORMANCE_FRAME_RATE.high : currentSetting == 2 ? VBG_PERFORMANCE_FRAME_RATE.balanced : VBG_PERFORMANCE_FRAME_RATE.low;
            }else{
                this.maxFramerate=currentSetting == 3 ? PERFORMANCE_FRAME_RATE.high : currentSetting == 2 ? PERFORMANCE_FRAME_RATE.balanced : PERFORMANCE_FRAME_RATE.low;
            }
            const averageFramerate = this.framerateSum / this.checksToAverage;

            const currentSettingName = currentSetting == 3 ? "high" : currentSetting == 2 ? "balanced" : "low";
            // console.log("Max Framerate: ", this.maxFramerate);
            // console.log("Average framerate: ", averageFramerate);
            const dropPercentage = ((this.maxFramerate - averageFramerate ) / this.maxFramerate) * 100;
            // console.log("Framerate change: ", dropPercentage.toFixed(1)+ "%");
            const dropThreshold = vb.effectPlaying ? VB_FRAMERATE_DROP_THRESHOLD_PERCENT[currentSettingName] : FRAMERATE_DROP_THRESHOLD_PERCENT[currentSettingName];
            if (dropPercentage > dropThreshold) {
                //drop situation
                //a genuine drop means the CPU is under strain again, so the
                //stable streak is broken.
                this.stableChecks = 0;
                if(this.isOptimizerChangable() && currentSetting > 1){
                    console.log("Framerate drop detected, changing optimizer to ", currentSetting-1);
                    applyPerformanceSetting(currentSetting - 1, false, false);
                }else{
                    console.log(`Skiping framerate drop, is optimizer changable: ${this.isOptimizerChangable()}, current setting: ${currentSetting}`);
                }
                //reset recovery checks since we need to have successive checks in a row
                this.succcessiveRecoveryChecks = 0;
                if(this.recoversCount >= STOP_RECOVER_THRESHOLD){
                    this.allowRecover=false;
                }
            }else if(dropPercentage < FRAMERATE_RECOVERY_THRESHOLD_PERCENT){
                //recover situation
                //the framerate is holding at the current tier: count it as a
                //stable check and re-arm auto-recovery once the CPU has clearly
                //recovered, so an earlier transient spike does not lock the
                //sender at a degraded tier for the rest of the session.
                this.registerStableCheck();
                this.succcessiveRecoveryChecks++;
                console.log("Framerate recovered checks: ", this.succcessiveRecoveryChecks)
                if(this.succcessiveRecoveryChecks >= FRAMERATE_RECOVERY_CHECKS){
                    if(this.isOptimizerChangable() && currentSetting < 3 && this.allowRecover){
                        console.log("Framerate recovered, changing optimizer to ", currentSetting+1);
                        applyPerformanceSetting(currentSetting + 1, false, false);
                        this.recoversCount++;
                    }else{
                        console.log(`Skiping framerate recover, is optimizer changable: ${this.isOptimizerChangable()}, current setting: ${currentSetting}, allow recover: ${this.allowRecover}`);
                    }
                    this.succcessiveRecoveryChecks = 0;
                }

            }else if(dropPercentage >= FRAMERATE_RECOVERY_THRESHOLD_PERCENT){
                //holding steady but not above the recovery threshold: still a
                //stable check (no drop), so it counts toward re-arming recovery.
                this.registerStableCheck();
                //reset recovery checks since we need to have successive checks in a row
                this.succcessiveRecoveryChecks = 0;
            }


            this.lastFramerate = averageFramerate;
            this.checkCount = 0;
            this.framerateSum = 0;
        }
    }

    isOptimizerChangable(){
        const {setting, lastChangedByUser} = getPerformanceState();
        return setting > 2 || !lastChangedByUser;
    }

    // Records a check during which the framerate did not drop. After enough
    // consecutive stable checks we consider the CPU pressure resolved and
    // re-enable auto-recovery (clearing the oscillation count and the
    // permanent lockout). This lets a session that briefly spiked climb back
    // to full quality instead of staying stuck at a reduced tier.
    registerStableCheck(){
        this.stableChecks++;
        if(this.stableChecks >= RECOVER_COUNT_RESET_CHECKS){
            if(!this.allowRecover || this.recoversCount > 0){
                console.log("Framerate stable, re-arming performance auto-recovery");
            }
            this.allowRecover = true;
            this.recoversCount = 0;
            this.stableChecks = 0;
        }
    }

    startMonitor() {
        // Monitor the framerate and change the optimizer based on the framerate drop
        setInterval(async () => {
            const framerate = await getSenderFrameRate();
            this.changeOptimizer(framerate);
        }, 4000);
    }
}
